import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Row, Col, Select, Checkbox, Upload, Space, Spin, Tag } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout';
import SmartEditor from '../../components/Common/SmartEditor';
import { draftService } from '../../services/draftService';

const { Dragger } = Upload;

const DraftModify: React.FC = () => {
  const { idx } = useParams<{ idx: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [sendMail, setSendMail] = useState(true);
  const [sendSMS, setSendSMS] = useState(true);
  const [receivers, setReceivers] = useState<any[]>([]);
  const [draftNo, setDraftNo] = useState('');
  const [editReason, setEditReason] = useState(''); // 수정 사유
  const [existingFiles, setExistingFiles] = useState<string[]>([]); // 기존 첨부파일

  // 상세보기에서 전달받은 수정 사유
  useEffect(() => {
    const reason = (location.state as any)?.editReason;
    if (reason) {
      setEditReason(reason);
    }
  }, [location]);

  // 기안서 데이터 로드
  useEffect(() => {
    if (idx) {
      fetchDraftData(parseInt(idx));
      fetchReceivers();
    }
  }, [idx]);

  const fetchDraftData = async (draftIdx: number) => {
    setDataLoading(true);
    try {
      const response = await draftService.getDraftDetail(draftIdx);
      if (response.resultCode === 200) {
        const data = response.data.info;

        // 기안번호 설정
        setDraftNo(data.Draft_No);

        // 기존 첨부파일 설정
        if (data.File_Names && data.File_Names.trim() !== '') {
          setExistingFiles(data.File_Names.split(', ').filter(f => f.trim() !== ''));
        }

        // Receiver 값 구성 (이메일, 전화번호 포함)
        // DB에서 가져온 값: Receiver, Receiver_Email, Receiver_Cell
        const receiverValue = data.Receiver_Email && data.Receiver_Cell
          ? `${data.Receiver}|${data.Receiver_Email}|${data.Receiver_Cell}`
          : data.Receiver;

        // 폼 데이터 설정
        form.setFieldsValue({
          Draft_Subject: data.Draft_Subject,
          Draft_Cost: data.Draft_Cost,
          Draft_Client: data.Draft_Client,
          Receiver: receiverValue,
        });

        setSendMail(data.Send_Mail_YN === 'Y' || data.Send_Mail_YN === 1);
        setSendSMS(data.Send_SMS_YN === 'Y' || data.Send_SMS_YN === 1);

        // HTML 엔티티 디코딩
        const decodeHTML = (html: string) => {
          const txt = document.createElement('textarea');
          txt.innerHTML = html;
          return txt.value;
        };

        // 스마트에디터에 내용 설정 (SET_IR 사용하여 교체)
        const setEditorContent = () => {
          if ((window as any).oEditors && (window as any).oEditors[0]) {
            // 먼저 에디터 내용 초기화
            (window as any).oEditors[0].exec('SET_IR', ['']);

            if (data.Draft_Contents) {
              const decodedContent = decodeHTML(data.Draft_Contents);
              // 내용 설정
              setTimeout(() => {
                (window as any).oEditors[0].exec('SET_IR', [decodedContent]);
                console.log('✅ 기안서 내용 로드 완료');
              }, 300);
            }
          } else {
            setTimeout(setEditorContent, 500);
          }
        };

        setTimeout(setEditorContent, 2000);
      }
    } catch (error) {
      console.error('기안서 조회 오류:', error);
      message.error('기안서를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setDataLoading(false);
    }
  };

  const fetchReceivers = async () => {
    try {
      const response = await fetch('http://localhost:3002/acghr/api/draft/init', {
        credentials: 'include'
      });
      const result = await response.json();
      if (result.resultCode === 200) {
        setReceivers(result.data.receivers || []);
      }
    } catch (error) {
      console.error('수신자 목록 로드 오류:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    // 수정 사유 확인
    if (!editReason || !editReason.trim()) {
      message.warning('수정 사유가 필요합니다. 상세보기에서 수정 버튼을 클릭해주세요.');
      return;
    }

    setLoading(true);
    try {
      const content = (window as any).getContent_smarteditor?.();

      const formData = new FormData();
      formData.append('Receiver', values.Receiver || '');
      formData.append('Draft_Subject', values.Draft_Subject);
      formData.append('Draft_Cost', values.Draft_Cost);
      formData.append('Draft_Client', values.Draft_Client);
      formData.append('editorTxt', content || '');
      formData.append('Edit_Reason', editReason);
      formData.append('Send_Mail_YN', sendMail ? 'Y' : 'N');
      formData.append('Send_SMS_YN', sendSMS ? 'Y' : 'N');

      // 새로 추가된 파일
      let uploadedFileCount = 0;
      console.log('📋 fileList:', fileList);
      fileList.forEach((file, idx) => {
        console.log(`  [${idx}] name:`, file.name, ', originFileObj:', file.originFileObj);
        if (file.originFileObj) {
          formData.append('fileInput', file.originFileObj);
          uploadedFileCount++;
          console.log('    ✅ 파일 추가됨:', file.name);
        } else {
          console.log('    ❌ originFileObj 없음');
        }
      });

      console.log('📤 수정 데이터:', {
        ...values,
        editReason,
        contentLength: content?.length,
        uploadedFileCount,
        totalFileListCount: fileList.length
      });

      // FormData 내용 확인
      console.log('📦 FormData has fileInput:', formData.has('fileInput'));

      // 기안서 수정 API 호출 (draftService 사용)
      const result = await draftService.updateDraft(parseInt(idx!), formData);

      if (result.resultCode === 200) {
        message.success('기안서가 수정되었습니다.');
        navigate('/draft/list');
      } else {
        message.error(result.message || '기안서 수정 실패');
      }
    } catch (error) {
      console.error('기안서 수정 오류:', error);
      message.error('기안서 수정 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    multiple: true,
    fileList,
    beforeUpload: (file: any) => {
      const uploadFile: UploadFile = {
        uid: `${Date.now()}-${file.name}`,
        name: file.name,
        status: 'done',
        originFileObj: file,
      };
      setFileList([...fileList, uploadFile]);
      return false;
    },
    onRemove: (file: UploadFile) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
  };

  if (dataLoading) {
    return (
      <MainLayout>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size="large" tip="기안서를 불러오는 중..." />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Card
        title="기안서 수정"
        extra={
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/draft/list')}>
            목록으로
          </Button>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* 1줄: 기안번호, 수신자, 메일/SMS, 비용, 지출처 */}
          <Row gutter={16}>
            <Col span={3}>
              <Form.Item label="기안번호">
                <Input disabled value={draftNo} />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                label="수신(결재자)"
                name="Receiver"
                rules={[{ required: true, message: '수신자 선택' }]}
              >
                <Select placeholder="선택">
                  {receivers.map((r) => (
                    <Select.Option
                      key={r.Admin_Name}
                      value={`${r.Admin_Name} ${r.Admin_Position}|${r.Admin_Email}|${r.Admin_Cell}`}
                    >
                      {r.Admin_Name} {r.Admin_Position}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="발송 옵션">
                <Space>
                  <Checkbox checked={sendMail} onChange={(e) => setSendMail(e.target.checked)}>
                    메일
                  </Checkbox>
                  <Checkbox checked={sendSMS} onChange={(e) => setSendSMS(e.target.checked)}>
                    SMS
                  </Checkbox>
                </Space>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                label="비용"
                name="Draft_Cost"
                rules={[{ required: true, message: '금액 필수' }]}
              >
                <Input type="number" placeholder="금액" addonAfter="원" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item label="지출처" name="Draft_Client">
                <Input placeholder="지출처" />
              </Form.Item>
            </Col>
          </Row>

          {/* 2줄: 제목 */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="제목"
                name="Draft_Subject"
                rules={[{ required: true, message: '제목을 입력해주세요' }]}
              >
                <Input placeholder="제목을 입력하세요" />
              </Form.Item>
            </Col>
          </Row>

          {/* 기존 첨부파일 */}
          {existingFiles.length > 0 && (
            <Form.Item label="기존 첨부파일">
              <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '12px',
                background: '#fafafa'
              }}>
                {existingFiles.map((file, index) => (
                  <div key={index} style={{
                    padding: '8px 12px',
                    marginBottom: index < existingFiles.length - 1 ? 8 : 0,
                    background: '#fff',
                    borderRadius: '4px',
                    border: '1px solid #e8e8e8',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>📎 {file}</span>
                    <Tag color="blue">기존 파일</Tag>
                  </div>
                ))}
              </div>
            </Form.Item>
          )}

          {/* 새 첨부파일 */}
          <Form.Item label="새 첨부파일 추가">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">클릭하거나 파일을 드래그하여 업로드</p>
              <p className="ant-upload-hint">
                새로운 파일을 추가할 수 있습니다.
              </p>
            </Dragger>
          </Form.Item>

          {/* 수정 사유 - 첨부파일 아래 */}
          <Form.Item label="수정 사유">
            <Input.TextArea
              value={editReason}
              disabled
              rows={2}
              style={{ background: '#f5f5f5' }}
            />
          </Form.Item>

          {/* 스마트에디터 */}
          <Form.Item label="내용">
            <SmartEditor id="smarteditor" height="400px" />
          </Form.Item>

          {/* 버튼 */}
          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                수정
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </MainLayout>
  );
};

export default DraftModify;
